import { ColDef, ColGroupDef } from 'ag-grid-community';
import { DropdownListRenderer } from 'src/app/shared/renderers/dropdown-list/dropdown-list.component';
import { InputSearchRenderer } from 'src/app/shared/renderers/input-search/input-search.component';
import { Utils } from 'src/app/shared/utils/utils';
import { Constants } from 'src/app/shared/utils/constants';
import { InputNumberRenderer } from 'src/app/shared/renderers/input-number/input-number.component';
import { ButtonExpandGridRenderer } from 'src/app/shared/renderers/button-expand-grid/button-expand-grid.component';
import { EEAGapSwash63LogicService, Swash63ValidationField } from '../eea-gap-swash63.logic.service';

export class EEAGapSurfaceWaterSwash63Coldef {
    constructor(private swash63LogicService: EEAGapSwash63LogicService) {}

    public getValidationColumn(field: Swash63ValidationField): Pick<ColDef, 'cellClassRules' | 'tooltipValueGetter'> {
        return {
            cellClassRules: {
                'swash63-validation-warning': params => !!this.swash63LogicService.getFieldWarning(params.data, field),
            },
            tooltipValueGetter: params => this.swash63LogicService.getFieldWarning(params.data, field),
        };
    }

    private readonly swash63ApplicationWindowColumns = {
        startDateHeaderName: 'Date Start',
        endDateHeaderName: 'Date End',
        dateColumnWidth: 150,
        hideJulianDays: true,
    };

    public getMainTableColumnsDefinition(options: any, isProjectOwnershipValid: boolean, bbchEarliestColumn: ColDef, columnDefContext: object): ColDef[] {
        return [
            {
                headerName: 'Direction of calculation',
                wrapText: true,
                field: Constants.GAP_FIELD_NAMES.APP_TIMING_DIRECTION,
                type: 'select',
                width: 140,
                resizable: true,
                editable: false,
                filter: 'agSetColumnfilter',
                cellRenderer: DropdownListRenderer,
                cellRendererParams: () => {
                    return {
                        options: Object.values(Constants.APP_TIMING_DIRECTIONS).map(direction => ({ value: direction })),
                        optionValue: 'value',
                        optionLabel: 'value',
                        columnThatModifies: Constants.GAP_FIELD_NAMES.APP_TIMING_DIRECTION,
                        disabled: !isProjectOwnershipValid,
                        parent: columnDefContext,
                    };
                },
                wrapHeaderText: true,
            },
            { ...bbchEarliestColumn, ...this.getValidationColumn('bbchEarliest') },
            {
                headerName: 'Allocated BBCH start',
                field: Constants.GAP_FIELD_NAMES.ALLOCATED_BBCH_START,
                type: 'default',
                width: 100,
                resizable: true,
                filter: 'agSetColumnFilter',
                editable: false,
                wrapHeaderText: true,
            },
            {
                headerName: 'BBCH end',
                wrapText: true,
                field: Constants.GAP_FIELD_NAMES.BBCH_LATEST,
                ...this.getValidationColumn('bbchLatest'),
                type: 'select',
                width: 90,
                resizable: true, filter: 'agSetColumnfilter',
                editable: false,
                valueGetter: (params: any) => this.swash63LogicService.isRowWithoutBbch(params.data)
                    ? 'n.s.'
                    : params.data?.bbchLatest,
                cellRenderer: InputSearchRenderer,
                cellRendererParams: (params: any) => {
                    return {
                        instance: columnDefContext,
                        options: options.bbch,
                        optionValue: 'value',
                        optionLabel: 'value',
                        columnThatModifies: Constants.GAP_FIELD_NAMES.BBCH_LATEST,
                        forceSelection: true,
                        disabled: !isProjectOwnershipValid ||
                            this.swash63LogicService.isRowWithoutBbch(params.data),
                    };
                }
            },
            {
                headerName: 'Allocated BBCH end',
                field: Constants.GAP_FIELD_NAMES.ALLOCATED_BBCH_END,
                type: 'default',
                filter: 'agSetColumnFilter',
                width: 105,
                resizable: true,
                editable: false,
                wrapHeaderText: true,
            },
            {
                headerName: 'Is PHI applicable?',
                wrapText: true,
                field: Constants.GAP_FIELD_NAMES.IS_PHI_APPLICABLE,
                type: 'select',
                width: 110,
                resizable: true,
                editable: false,
                filter: 'agSetColumnfilter',
                filterParams: {
                    valueFormatter: (params: any) => {
                        return params.value ? Constants.AFFIRMATIVE_VALUES.YES : !Utils.isEmptyValue(params.value) ? Constants.AFFIRMATIVE_VALUES.NO : '(Blanks)';
                    }
                },
                cellRenderer: DropdownListRenderer,
                cellRendererParams: () => {
                    return {
                        options: [{ value: true, key: Constants.AFFIRMATIVE_VALUES.YES }, { value: false, key: Constants.AFFIRMATIVE_VALUES.NO }],
                        columnThatModifies: Constants.GAP_FIELD_NAMES.IS_PHI_APPLICABLE,
                        disabled: !isProjectOwnershipValid,
                        optionLabel: 'key',
                        optionValue: 'value',
                        parent: columnDefContext,
                    };
                },
                wrapHeaderText: true,
            },
            {
                headerName: 'PHI (days)',
                wrapText: true,
                field: Constants.GAP_FIELD_NAMES.PHI_DAYS,
                type: 'default',
                width: 100,
                resizable: true,
                editable: false,
                filter: 'agSetColumnfilter',
                cellRenderer: InputNumberRenderer,
                cellRendererParams: (params: any) => {
                    return {
                        disabled: !isProjectOwnershipValid || !params.data.isPhiApplicable,
                        min: 0,
                        maxDecimals: 0,
                        useGrouping: false,
                    };
                },
                cellStyle: (params: any): { [key: string]: string } | null => {
                    return !isProjectOwnershipValid || !params.data.isPhiApplicable ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' } : null;
                },
                wrapHeaderText: true,
            },
        ];
    }

    public getExpandButtonColumn(isProjectOwnershipValid: boolean, geographySelected: string, columnDefContext: object): ColDef {
        return {
            headerName: 'Multiple Application details',
            field: 'expandButton',
            minWidth: 150,
            width: 150,
            cellRenderer: ButtonExpandGridRenderer,
            cellRendererParams: (params: any) => ({
                context: columnDefContext,
                dataComponent: columnDefContext,
                showLabel: 'Enter Inputs',
                hideLabel: 'Hide Inputs',
                showIcon: 'pi pi-arrow-down',
                hideIcon: 'pi pi-arrow-right',
                visible: params.data.numberOfApplications >= 2 && isProjectOwnershipValid,
            }),
            resizable: true,
            suppressSizeToFit: true,
            hide: geographySelected !== Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
            type: 'default',
            autoHeight: true,
            editable: false,
            cellStyle: (params: any): { [key: string]: string } | null => {
                if (!(params.data.numberOfApplications >= 2)) {
                    return { pointerEvents: 'none', backgroundColor: '#f0f0f0' };
                }
                return !isProjectOwnershipValid ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' } : { pointerEvents: 'auto', backgroundColor: '' };
            },
            wrapText: true,
            wrapHeaderText: true,
        };
    }

    public getPrzmAndStep4ColumnGroups(przmColumns: ColDef[], step4Column: ColDef): ColGroupDef[] {
        return [
            { headerName: 'PRZM input', children: przmColumns.map(column => this.withPrzmAvailability(column)) },
            { headerName: '', children: [step4Column] },
        ];
    }

    public getInterceptionValidationColumn(): Pick<ColDef, 'cellClassRules' | 'tooltipValueGetter'> {
        const warning = (data: any): string => this.swash63LogicService.getInterceptionWarnings(data).join('\n');
        return {
            cellClassRules: {
                'swash63-validation-warning': params => !!warning(params.data),
            },
            tooltipValueGetter: params => warning(params.data),
        };
    }

    private getDetailInterceptionValidationColumn(): Pick<ColDef, 'cellClassRules' | 'tooltipValueGetter'> {
        const warning = (data: any): string => this.swash63LogicService.getDetailInterceptionWarnings(data).join('\n');
        return {
            cellClassRules: {
                'swash63-validation-warning': params => !!warning(params.data),
            },
            tooltipValueGetter: params => warning(params.data),
        };
    }

    private isFixedApplicationInterval(params: any): boolean {
        const parentRow = params?.context?.gridApi?.getRowNode(params.data?.parentRowNodeId)?.data;
        const row = parentRow ?? params?.data;
        const direction = row?.appTimingDirection ?? params?.data?.appTimingDirection;
        const applicationNumber = Number(params?.data?.application_number);
        const applicationCount = Number(row?.numberOfApplications ?? params?.data?.numberOfApplications);

        return (direction === Constants.APP_TIMING_DIRECTIONS.FORWARD && applicationNumber === 1) ||
            (direction === Constants.APP_TIMING_DIRECTIONS.BACKWARD && applicationNumber === applicationCount);
    }

    private withPrzmAvailability(column: ColDef): ColDef {
        const baseParams = column.cellRendererParams;
        const isDisabled = (data: any) => !this.swash63LogicService.isPrzmInputEnabled(data);
        return {
            ...column,
            headerTooltip: 'Enabled only when at least one R scenario is selected in the Application Window.',
            cellRendererParams: (params: any) => {
                const resolved = typeof baseParams === 'function' ? baseParams(params) : baseParams;
                return { ...resolved, disabled: !!resolved?.disabled || isDisabled(params.data) };
            },
            cellStyle: (params: any): { [key: string]: string } | null =>
                isDisabled(params.data) ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' } : null,
        };
    }

    public getDetailGridColumnsDefinition(options: any, isProjectOwnershipValid: boolean): ColGroupDef[] {
        return [
            {
                headerName: '',
                children: [
                    {
                        headerName: 'Application Number',
                        field: 'application_number',
                        width: 110,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        editable: false,
                        sort: 'asc',
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: () => ({ disabled: true }),
                    },
                    {
                        headerName: 'Application Interval (days)',
                        field: 'min_application_interval',
                        width: 150,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        type: 'default',
                        editable: false,
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: (params: any) => ({
                            disabled: !isProjectOwnershipValid || this.isFixedApplicationInterval(params),
                            min: 0,
                            maxDecimals: 0,
                            useGrouping: false,
                        }),
                        cellStyle: (params: any): { [key: string]: string } | null =>
                            !isProjectOwnershipValid || this.isFixedApplicationInterval(params)
                                ? { pointerEvents: 'none', backgroundColor: '#f0f0f0' }
                                : null,
                    },
                ],
            },
            {
                headerName: 'Rate (g/ha)',
                children: [
                    {
                        headerName: 'Default Active Ingredient',
                        field: 'defaultAI',
                        cellRenderer: '',
                        editable: !isProjectOwnershipValid,
                    },
                ],
            },
            {
                headerName: '',
                children: [
                    {
                        headerName: 'Interception (%)',
                        field: 'crop_interception',
                        type: 'default',
                        width: 130,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        editable: false,
                        ...this.getDetailInterceptionValidationColumn(),
                        cellRenderer: InputNumberRenderer,
                        cellRendererParams: () => ({ disabled: !isProjectOwnershipValid }),
                    },
                    {
                        headerName: 'Application Method',
                        field: 'application_method',
                        type: 'select',
                        width: 200,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        editable: false,
                        cellRenderer: DropdownListRenderer,
                        cellRendererParams: () => ({
                            options: options.applicationMethods,
                            optionValue: 'value',
                            optionLabel: 'value',
                            disabled: !isProjectOwnershipValid,
                        }),
                    },
                ],
            },
        ];
    }

    public getApplicationWindowColumnsDefinition(bbchOptions: any[], isProjectOwnershipValid: boolean): {
        bbchStartColumn: ColDef;
        bbchEndColumn: ColDef;
        startDateHeaderName: string;
        endDateHeaderName: string;
        dateColumnWidth: number;
        hideJulianDays: boolean;
    } {
        const bbchColumn = (headerName: string, field: 'bbchStart' | 'bbchEnd'): ColDef => ({
            headerName,
            field,
            ...this.getValidationColumn(field),
            wrapText: true,
            type: 'select',
            width: 110,
            resizable: true,
            filter: 'agSetColumnFilter',
            editable: false,
            cellRenderer: InputSearchRenderer,
            cellRendererParams: (params: any) => ({
                options: params?.data?.allocatedBbchOptions?.length ? params.data.allocatedBbchOptions : bbchOptions,
                optionValue: 'value',
                optionLabel: 'value',
                columnThatModifies: field,
                forceSelection: true,
                disabled: true,
            }),
        });

        return {
            bbchStartColumn: bbchColumn('Allocated BBCH start', 'bbchStart'),
            bbchEndColumn: bbchColumn('Allocated BBCH end', 'bbchEnd'),
            ...this.swash63ApplicationWindowColumns,
        };
    }
}
